<template>
  <div class="inventory-health-page nx-page nx-report-page">
    <nx-report-header
      title="库存健康度综合评估"
    >
      <el-date-picker
        v-model="queryMonth"
        type="month"
        value-format="yyyy-MM"
        placeholder="选择月份"
        @change="handleQuery"
      />
    </nx-report-header>

    <el-row :gutter="16" class="kpi-row" v-loading="overviewLoading">
      <el-col :xs="24" :sm="8">
        <el-card shadow="never" class="kpi-card">
          <div class="kpi-label">库存总健康度</div>
          <div class="kpi-value">{{ formatDecimal(overview.avgScore) }}<span class="unit">分</span></div>
          <div :class="['kpi-trend', trendClass(overview.scoreChange, true)]">
            <i :class="trendIcon(overview.scoreChange, true)"></i>
            较上月{{ trendText(overview.scoreChange, true) }} {{ formatDecimal(Math.abs(overview.scoreChange || 0)) }} 分
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card shadow="never" class="kpi-card">
          <div class="kpi-label">需关注煤种数量</div>
          <div class="kpi-value warn">{{ focusCount }}<span class="unit">个</span></div>
          <div :class="['kpi-trend', trendClass(overview.focusChange, false)]">
            <i :class="trendIcon(overview.focusChange, false)"></i>
            较上月{{ trendText(overview.focusChange, false) }} {{ focusChangeValue }} 个
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card shadow="never" class="kpi-card">
          <div class="kpi-label">平均库存周转率</div>
          <div class="kpi-value">{{ formatDecimal(avgTurnoverRate) }}<span class="unit">次/月</span></div>
          <div :class="['kpi-trend', trendClass(overview.turnoverChange, false)]">
            <i :class="trendIcon(overview.turnoverChange, false)"></i>
            较上月{{ trendText(overview.turnoverChange, false) }} {{ formatDecimal(Math.abs(overview.turnoverChange || 0)) }} 次
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="suggest-card">
      <div class="card-title">改进建议</div>
      <el-row :gutter="12" class="suggest-row">
        <el-col :xs="24" :sm="8" v-for="item in topSuggestions" :key="item.id">
          <div :class="['suggest-item', `suggest-${suggestType(item)}`]">
            <div class="suggest-title">
              <i :class="suggestIcon(item)"></i>
              <span>{{ item.title }}</span>
            </div>
            <p class="suggest-content">{{ item.content }}</p>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="card-head">
        <span class="card-title">煤种明细</span>
        <el-input
          v-model="keyword"
          size="small"
          clearable
          placeholder="搜索煤种"
          prefix-icon="el-icon-search"
          class="table-search"
        />
      </div>
      <div class="table-scroll">
        <el-table
          v-loading="tableLoading"
          :data="filteredRecords"
          style="width: 100%"
          :header-cell-style="{ background: '#f8f9fb', color: '#909399' }"
        >
          <el-table-column prop="coalType" label="货主/煤种" min-width="220">
            <template slot-scope="{ row }">{{ displayCoalType(row) }}</template>
          </el-table-column>
          <el-table-column prop="avgInventory" label="月均库存" min-width="130" align="center">
            <template slot-scope="{ row }">{{ formatDecimal(row.avgInventory) }} 吨</template>
          </el-table-column>
          <el-table-column prop="totalScore" label="健康度评分" min-width="140" align="center" />
          <el-table-column prop="ageDays" label="堆存时长" min-width="140" align="center">
            <template slot-scope="{ row }">{{ row.ageDays }} 天</template>
          </el-table-column>
          <el-table-column prop="turnoverRate" label="周转率" min-width="140" align="center">
            <template slot-scope="{ row }">{{ formatDecimal(row.turnoverRate) }} 次/月</template>
          </el-table-column>
          <el-table-column prop="level" label="状态" min-width="120" align="center">
            <template slot-scope="{ row }">
              <el-tag :class="['status-tag', `status-${row.level}`]" size="mini">
                {{ levelLabel(row.level) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="120" align="center">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" @click="openDetail(row)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="query.size"
        :current-page.sync="query.page"
        @current-change="loadDetailPage"
      />
    </el-card>

    <el-dialog
      title="煤种健康详情"
      :visible.sync="detailVisible"
      width="560px"
      append-to-body
      destroy-on-close
    >
      <el-descriptions v-if="currentDetail" :column="2" border class="health-detail">
        <el-descriptions-item label="货主名称">{{ detailText(currentDetail.custName) }}</el-descriptions-item>
        <el-descriptions-item label="煤种名称">{{ detailText(currentDetail.cargoName || currentDetail.coalType) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ levelLabel(currentDetail.level) }}</el-descriptions-item>
        <el-descriptions-item label="健康度评分">{{ formatDecimal(currentDetail.totalScore) }} 分</el-descriptions-item>
        <el-descriptions-item label="月均库存">{{ formatDecimal(currentDetail.avgInventory) }} 吨</el-descriptions-item>
        <el-descriptions-item label="累计入库">{{ formatDecimal(currentDetail.inWeight) }} 吨</el-descriptions-item>
        <el-descriptions-item label="累计出库">{{ formatDecimal(currentDetail.outWeight) }} 吨</el-descriptions-item>
        <el-descriptions-item label="堆存时长">{{ detailText(currentDetail.ageDays, ' 天') }}</el-descriptions-item>
        <el-descriptions-item label="周转率">{{ formatDecimal(currentDetail.turnoverRate) }} 次/月</el-descriptions-item>
        <el-descriptions-item label="库存稳定性">{{ formatDecimal(currentDetail.stability) }}</el-descriptions-item>
        <el-descriptions-item label="堆存时长评分">{{ formatDecimal(currentDetail.ageScore) }} 分</el-descriptions-item>
        <el-descriptions-item label="周转率评分">{{ formatDecimal(currentDetail.turnoverScore) }} 分</el-descriptions-item>
        <el-descriptions-item label="稳定性评分">{{ formatDecimal(currentDetail.stabilityScore) }} 分</el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchHealthOverview,
  fetchHealthDetailPage,
  fetchImproveSuggestions
} from './api'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'

function defaultMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function monthRange(month) {
  const [year, monthNum] = month.split('-').map((i) => Number(i))
  const startDate = `${year}-${String(monthNum).padStart(2, '0')}-01`
  const end = new Date(year, monthNum, 0).getDate()
  const endDate = `${year}-${String(monthNum).padStart(2, '0')}-${String(end).padStart(2, '0')}`
  return {
    startTime: `${startDate} 00:00:00`,
    endTime: `${endDate} 23:59:59`
  }
}

export default {
  name: 'InventoryHealth',
  components: {
    NxReportHeader
  },
  data() {
    return {
      queryMonth: defaultMonth(),
      query: {
        page: 1,
        size: 10
      },
      keyword: '',
      overviewLoading: false,
      overview: {
        avgScore: 0,
        greenCount: 0,
        yellowCount: 0,
        redCount: 0,
        coalTypeCount: 0
      },
      suggestions: [],
      tableLoading: false,
      tableRecords: [],
      tableTotal: 0,
      detailVisible: false,
      currentDetail: null
    }
  },
  computed: {
    focusCount() {
      return Number(this.overview.yellowCount || 0) + Number(this.overview.redCount || 0)
    },
    avgTurnoverRate() {
      if (!this.tableRecords.length) return 0
      const total = this.tableRecords.reduce((sum, item) => sum + Number(item.turnoverRate || 0), 0)
      return Number((total / this.tableRecords.length).toFixed(1))
    },
    focusChangeValue() {
      if (typeof this.overview.focusChange !== 'undefined') return Math.abs(Number(this.overview.focusChange || 0))
      return this.focusCount
    },
    topSuggestions() {
      return this.suggestions.slice(0, 3)
    },
    filteredRecords() {
      const word = (this.keyword || '').trim()
      if (!word) return this.tableRecords
      return this.tableRecords.filter((item) => {
        return [item.coalType, item.custName, item.cargoName].some((text) => (text || '').includes(word))
      })
    }
  },
  mounted() {
    this.handleQuery()
  },
  methods: {
    formatDecimal(value) {
      return Number(value || 0).toFixed(1)
    },
    detailText(value, suffix = '') {
      if (value === null || value === undefined || value === '') return '-'
      return `${value}${suffix}`
    },
    displayCoalType(row) {
      if (!row) return '-'
      if (row.coalType) return row.coalType
      const custName = row.custName || '未命名货主'
      const cargoName = row.cargoName || '未命名煤种'
      return `${custName}/${cargoName}`
    },
    openDetail(row) {
      this.currentDetail = { ...row }
      this.detailVisible = true
    },
    buildPayload() {
      if (!this.queryMonth) {
        this.$message.warning('请选择月份')
        return null
      }
      const t = monthRange(this.queryMonth)
      return {
        startTime: t.startTime,
        endTime: t.endTime
      }
    },
    async handleQuery() {
      const payload = this.buildPayload()
      if (!payload) return
      this.query.page = 1
      await Promise.all([this.loadOverview(payload), this.loadSuggestions(payload), this.loadDetailPage()])
    },
    async loadOverview(payload) {
      this.overviewLoading = true
      try {
        const { data } = await fetchHealthOverview(payload)
        this.overview = data.data || this.overview
      } catch (e) {
        this.$message.error('健康概览加载失败')
      } finally {
        this.overviewLoading = false
      }
    },
    async loadSuggestions(payload) {
      try {
        const { data } = await fetchImproveSuggestions(payload)
        this.suggestions = (data.data && data.data.suggestions) || []
      } catch (e) {
        this.suggestions = []
      }
    },
    async loadDetailPage() {
      const payload = this.buildPayload()
      if (!payload) return
      this.tableLoading = true
      try {
        const { data } = await fetchHealthDetailPage({
          ...payload,
          page: this.query.page,
          size: this.query.size
        })
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    levelLabel(level) {
      if (level === 'green') return '健康'
      if (level === 'yellow') return '警告'
      return '危险'
    },
    suggestType(item) {
      if (item.priority === '高') return 'danger'
      if (item.priority === '中') return 'warning'
      return 'success'
    },
    suggestIcon(item) {
      if (item.priority === '高') return 'el-icon-warning'
      if (item.priority === '中') return 'el-icon-warning-outline'
      return 'el-icon-success'
    },
    trendClass(changeValue, riseIsGood) {
      const n = Number(changeValue || 0)
      if (n === 0) return 'normal'
      const isGood = riseIsGood ? n > 0 : n < 0
      return isGood ? 'success' : 'danger'
    },
    trendIcon(changeValue, riseIsGood) {
      const n = Number(changeValue || 0)
      if (n === 0) return 'el-icon-minus'
      const isGood = riseIsGood ? n > 0 : n < 0
      return isGood ? 'el-icon-top' : 'el-icon-bottom'
    },
    trendText(changeValue, riseIsGood) {
      const n = Number(changeValue || 0)
      if (n === 0) return '持平'
      const isGood = riseIsGood ? n > 0 : n < 0
      return isGood ? '提升' : '下降'
    }
  }
}
</script>

<style scoped>
.inventory-health-page {
  overflow-x: hidden;
}
.crumb {
  margin-bottom: 10px;
  color: #909399;
  font-size: 12px;
}
.sep {
  margin: 0 6px;
}
.page-head {
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.page-title {
  margin: 0;
  font-size: 36px;
  font-weight: 600;
  color: #1f2329;
  line-height: 1.1;
}
/* 恢复本页时间组件为 Element 默认中等尺寸 */
.nx-report-header ::v-deep .el-date-editor.el-input,
.nx-report-header ::v-deep .el-date-editor.el-input__inner {
  width: 220px;
}
.nx-report-header ::v-deep .el-date-editor .el-input__inner {
  height: 40px;
  line-height: 40px;
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi-row .el-col {
  margin-bottom: 10px;
}
.kpi-card {
  min-height: 128px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.kpi-label {
  color: #606266;
  font-size: 14px;
}
.kpi-value {
  margin-top: 10px;
  color: #111827;
  font-size: 42px;
  font-weight: 700;
  line-height: 1;
}
.kpi-value.warn {
  color: #e6a23c;
}
.unit {
  margin-left: 2px;
  font-size: 16px;
  font-weight: 500;
  color: #606266;
}
.kpi-trend {
  margin-top: 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.kpi-trend.danger {
  color: #f56c6c;
}
.kpi-trend.success {
  color: #67c23a;
}
.kpi-trend.normal {
  color: #909399;
}
.suggest-card {
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.suggest-row {
  margin-top: 12px;
}
.suggest-row .el-col {
  margin-bottom: 8px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2329;
}
.suggest-item {
  min-height: 96px;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 12px 14px;
}
.suggest-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1f2329;
  font-weight: 600;
}
.suggest-content {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.55;
}
.suggest-danger {
  background: #fff2f0;
  border-color: #fbc4c4;
}
.suggest-danger .suggest-title,
.suggest-danger .suggest-content {
  color: #cf1322;
}
.suggest-warning {
  background: #fffbe6;
  border-color: #f7df9a;
}
.suggest-warning .suggest-title,
.suggest-warning .suggest-content {
  color: #ad6800;
}
.suggest-success {
  background: #f0f9eb;
  border-color: #c6e7b8;
}
.suggest-success .suggest-title,
.suggest-success .suggest-content {
  color: #389e0d;
}
.table-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.table-search {
  width: 208px;
}
.table-scroll {
  margin-top: 10px;
}
.table-scroll ::v-deep .el-table th {
  height: 46px;
}
.table-scroll ::v-deep .el-table td {
  height: 52px;
}
.status-tag {
  border: none;
  border-radius: 4px;
  padding: 0 8px;
}
.status-green {
  background: #f0f9eb;
  color: #67c23a;
}
.status-yellow {
  background: #fdf6ec;
  color: #e6a23c;
}
.status-red {
  background: #fef0f0;
  color: #f56c6c;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
